import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, MenuItem,
  Autocomplete, CircularProgress, Alert, Tooltip, Divider, Checkbox, FormControlLabel,
} from '@mui/material';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import AutoFixHighRounded from '@mui/icons-material/AutoFixHighRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import LocalPrintshopRounded from '@mui/icons-material/LocalPrintshopRounded';

import {
  listInkPurchaseOrders, getInkPurchaseOrder, createInkPurchaseOrder, updateInkPurchaseOrder,
  issueInkPurchaseOrder, cancelInkPurchaseOrder, createInkReceipt,
  listInkSuppliers, listInkProducts, listInkSeries, getInkStock, getInkDemandSummary, getInkOnOrder,
  InkPurchaseOrder, InkPoStatus, InkPoLine, InkPoLineInput, InkPoInput, InkProduct, InkBatch,
  InkDemandJob,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

// ---------------------------------------------------------------------------
// Shapes this screen reads that the shared API types don't carry yet
// (migration 143 + the PO handlers in blueprints/ink_stock.py).
// ---------------------------------------------------------------------------

type PoLine = InkPoLine & {
  expected_date?: string | null;            // this line's own date; null = header's
  effective_expected_date?: string | null;  // COALESCE(line, header) — what to show
  pack_size_kg?: number | null;
  packs_ordered?: number | null;
  packs_received?: number | null;
  packs_outstanding?: number | null;
};
type Po = Omit<InkPurchaseOrder, 'lines'> & { supplier_lead_time_days?: number | null; lines?: PoLine[] };
type PoLineInput = InkPoLineInput & { id?: number; expected_date?: string | null };
type ReceiptBatch = InkBatch & { outstanding_after_kg?: number | null; po_status?: InkPoStatus };

// Display rule for this screen: never more than 1 decimal on kg or $/kg.
const fmt1 = (v: number | string) =>
  Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const kg = (v: number | string | null | undefined) => (v == null ? '—' : `${fmt1(v)} kg`);
const perKg = (v: number | string | null | undefined) => (v == null ? '—' : `$${fmt1(v)}/kg`);
const money = (v: number | null | undefined) =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tubs = (n: number | null | undefined) =>
  n == null ? null : `${fmt1(n)} ${Math.abs(Number(n)) === 1 ? 'tub' : 'tubs'}`;
const dateOf = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString() : '—');

const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** A product's pack size, or null when it has none (ordered by kg instead). */
const packSizeOf = (p: { pack_size_kg?: number | null } | null | undefined) => {
  const n = Number(p?.pack_size_kg);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The provenance stamp a fill-from-shortfall writes into a PO's notes.
 *
 * Reads as a dated snapshot, not a live link, because that is what it is: the
 * jobs are a forecast taken on the day the order was raised, and any of them
 * could later be rescheduled, re-run at a different quantity, or cancelled. A
 * structured job foreign key would quietly become a lie; a note stays honest.
 *
 *   Auto-filled from shortfall on 15/09/2026.
 *   Covers 2 jobs:
 *     284165  MCA  MCA4053467  75,000 sh  PDL2035FECOLMQ, BFECOLMQ
 *     283860  MCA  MCA4053467  40,000 sh  PDL2035FECOLMQ
 *   Quantities are sized to these run lengths — check them if the order looks wrong.
 */
function shortfallStamp(covered: { job: InkDemandJob; inks: Set<string> }[]): string {
  const when = new Date().toLocaleDateString('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  const rows = [...covered]
    .sort((a, b) => a.job.job_number.localeCompare(b.job.job_number))
    .map(({ job, inks }) => {
      const sheets = job.run_length_sheets != null
        ? `${job.run_length_sheets.toLocaleString()} sh`
        : 'run length unknown';
      return [
        job.job_number,
        job.customer_code ?? '',
        job.customer_ref ?? '',
        sheets,
        Array.from(inks).sort().join(', '),
      ].filter(Boolean).join('  ');
    });
  return [
    `Auto-filled from shortfall on ${when}.`,
    `Covers ${covered.length} job${covered.length === 1 ? '' : 's'}:`,
    ...rows.map((r) => `  ${r}`),
    'Quantities are sized to these run lengths — check them if the order looks wrong.',
  ].join('\n');
}

const STATUS_STYLE: Record<InkPoStatus, { label: string; colour: string; bg: string }> = {
  draft: { label: 'DRAFT', colour: TOKENS.ink3, bg: 'rgba(0,0,0,0.06)' },
  issued: { label: 'ISSUED', colour: '#1565c0', bg: 'rgba(21,101,192,0.10)' },
  received: { label: 'RECEIVED', colour: '#2e7d32', bg: 'rgba(46,125,50,0.10)' },
  cancelled: { label: 'CANCELLED', colour: '#b0413e', bg: 'rgba(176,65,62,0.10)' },
};
const WARN = '#b26a00';

function StatusChip({ status }: { status: InkPoStatus }) {
  const s = STATUS_STYLE[status];
  return <Chip size="small" label={s.label}
    sx={{ height: 18, fontSize: 9.5, fontWeight: 800, color: s.colour, bgcolor: s.bg }} />;
}

/**
 * InkPurchaseOrdersView — raise, issue, amend, receive against, and cancel ink
 * POs (mission brief Phase 5). A PO is a draft you build up, then issue to place
 * it; issuing stamps the order date and defaults the expected date from the
 * supplier's lead time. Lines are ordered in whole tubs (kg derives from the
 * SKU's pack size). An issued PO can be AMENDED — dates, quantities and prices,
 * as the supplier confirms them. Receiving is done HERE, line by line, from the
 * PO detail: each Receive books a batch into the store and draws the line down.
 */
export default function InkPurchaseOrdersView({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<InkPoStatus | ''>('');
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; po?: Po } | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const listQ = useQuery({
    queryKey: productionKeys.inkPurchaseOrders({ supplier: supplierFilter || undefined, status: statusFilter || undefined }),
    queryFn: () => listInkPurchaseOrders({ supplier: supplierFilter || undefined, status: statusFilter || undefined }),
    staleTime: 30 * 1000,
  });
  const suppliersQ = useQuery({ queryKey: productionKeys.inkSuppliers(), queryFn: () => listInkSuppliers(), staleTime: 5 * 60 * 1000 });

  const orders = listQ.data ?? [];
  const openCount = orders.filter((o) => o.status === 'issued').length;
  const openValue = orders.filter((o) => o.status === 'issued').reduce((t, o) => t + Number(o.total_value || 0), 0);

  // A PO write moves On order (and, on receipt, stock). Invalidate broadly.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['production', 'ink-purchase-orders'] });
    qc.invalidateQueries({ queryKey: productionKeys.inkOnOrder() });
  };

  return (
    <Box sx={embedded ? { p: 0, maxWidth: 1500 } : { p: { xs: 1.5, md: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
          <ReceiptLongRounded sx={{ color: TOKENS.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink purchase orders</Typography>
        </Stack>
      )}

      {/* headline + new PO */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
        gap: 2, p: 2, mb: 2, borderRadius: '12px', bgcolor: TOKENS.lineSoft,
      }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>Open orders</Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: TOKENS.accent, lineHeight: 1.15 }}>{openCount}</Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>issued, awaiting delivery</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>On order value</Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15 }}>{money(openValue)}</Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>ordered cost, issued POs</Typography>
          </Box>
          <Box sx={{ alignSelf: 'center', maxWidth: 360 }}>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
              <LocalShippingRounded sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5 }} />
              Deliveries are received <b>here</b>: open an issued PO and press <b>Receive</b> on the line that arrived.
            </Typography>
          </Box>
        </Stack>
        <Button size="small" variant="contained" startIcon={<AddRounded />}
          onClick={() => setEditing({ mode: 'create' })}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          New purchase order
        </Button>
      </Box>

      {/* filters */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <TextField select size="small" label="Supplier" value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)} sx={{ width: 200 }}>
          <MenuItem value="">All suppliers</MenuItem>
          {(suppliersQ.data ?? []).map((s) => <MenuItem key={s.supplier_code} value={s.supplier_code}>{s.supplier_code}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InkPoStatus | '')} sx={{ width: 160 }}>
          <MenuItem value="">All</MenuItem>
          {(['draft', 'issued', 'received', 'cancelled'] as InkPoStatus[]).map((s) =>
            <MenuItem key={s} value={s}>{STATUS_STYLE[s].label}</MenuItem>)}
        </TextField>
      </Stack>

      {listQ.isLoading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={26} /></Stack>
      ) : listQ.isError ? (
        <Alert severity="error">Couldn’t load purchase orders: {(listQ.error as Error)?.message}</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['PO', 'Supplier', 'Status', 'Lines'].map((h) =>
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>)}
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Ordered</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Outstanding</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Value</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Ordered on</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Expected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} hover onClick={() => setDetailId(o.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12.5 }}>{o.po_number}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{o.supplier_code}</TableCell>
                  <TableCell><StatusChip status={o.status} /></TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>{o.line_count}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>{kg(o.total_ordered_kg)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700, color: Number(o.outstanding_kg) > 0 ? TOKENS.ink : TOKENS.ink3 }}>
                    {Number(o.outstanding_kg) > 0 ? kg(o.outstanding_kg) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: TOKENS.ink2 }}>{money(o.total_value)}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: TOKENS.ink3 }}>{dateOf(o.order_date)}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: TOKENS.ink3 }}>{dateOf(o.expected_date)}</TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={9} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  No purchase orders{supplierFilter || statusFilter ? ' match the filters' : ' yet — raise one with “New purchase order”'}.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {editing && (
        <PoDialog mode={editing.mode} po={editing.po}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { setEditing(null); invalidate(); setDetailId(saved.id); }} />
      )}
      {detailId != null && (
        <PoDetailDialog poId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={invalidate}
          onEdit={(po) => { setDetailId(null); setEditing({ mode: 'edit', po }); }} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Create / edit a draft PO, or amend an issued one.
// ---------------------------------------------------------------------------

interface DraftLine {
  key: string;
  id?: number;          // set when editing an existing line
  ink_key: string;
  tubs: string;         // what the operator types — whole packs
  kg: string;           // only used when the SKU has no pack size
  cost: string;
  expected: string;     // per-line expected date; '' = same as the PO
  received?: number;    // kg already received (amend mode — the floor for qty)
}

let _lk = 0;
const newLine = (): DraftLine => ({ key: `l${_lk++}`, ink_key: '', tubs: '', kg: '', cost: '', expected: '' });

/** Ordered kg for a line: tubs × pack size, else the typed kg for a packless SKU. */
function lineKg(l: DraftLine, pack: number | null): number {
  if (pack) {
    const t = parseFloat(l.tubs);
    return Number.isFinite(t) && t > 0 ? round1(t * pack) : 0;
  }
  const n = parseFloat(l.kg);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function PoDialog({ mode, po, onClose, onSaved }: {
  mode: 'create' | 'edit';
  po?: Po;
  onClose: () => void;
  onSaved: (po: InkPurchaseOrder) => void;
}) {
  const amend = mode === 'edit' && po?.status === 'issued';

  const suppliersQ = useQuery({ queryKey: productionKeys.inkSuppliers(), queryFn: () => listInkSuppliers(), staleTime: 5 * 60 * 1000 });
  const productsQ = useQuery({ queryKey: productionKeys.inkProducts({ materialClass: 'ink' }), queryFn: () => listInkProducts({ materialClass: 'ink' }), staleTime: 5 * 60 * 1000 });
  const products = useMemo(() => productsQ.data ?? [], [productsQ.data]);
  const productByKey = useMemo(() => {
    const m = new Map<string, InkProduct>();
    products.forEach((p) => m.set(p.ink_key, p));
    return m;
  }, [products]);

  const [supplier, setSupplier] = useState(po?.supplier_code ?? '');
  const [expectedDate, setExpectedDate] = useState(po?.expected_date ?? '');
  // No Notes INPUT (Jack, 09/09) — but the field is still written to, by
  // fill-from-shortfall, which stamps what the order is for. Carried through an
  // amend so an existing note is never silently wiped.
  const [notes, setNotes] = useState(po?.notes ?? '');
  const [lines, setLines] = useState<DraftLine[]>(() => {
    if (!po?.lines?.length) return [newLine()];
    return po.lines.map((l) => {
      const pack = packSizeOf(l);
      const q = Number(l.qty_ordered_kg);
      return {
        key: `l${_lk++}`, id: l.id, ink_key: l.ink_key,
        tubs: pack ? String(round1(q / pack)) : '',
        kg: String(q),
        cost: l.unit_cost_per_kg == null ? '' : String(l.unit_cost_per_kg),
        expected: l.expected_date ?? '',
        received: Number(l.qty_received_kg ?? 0),
      };
    });
  });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  /** The jobs behind a fill-from-shortfall, so the buyer can see what they're buying for. */
  const [coveredJobs, setCoveredJobs] = useState<{ job: InkDemandJob; inks: Set<string> }[]>([]);

  const supplierRow = (suppliersQ.data ?? []).find((s) => s.supplier_code === supplier);
  const leadDays = supplierRow?.lead_time_days ?? null;
  // Order date is the ISSUE date (server-side). For the default expected date
  // preview: the real order date once issued, else today.
  const orderBase = po?.order_date ?? isoToday();
  const defaultExpected = leadDays != null ? addDays(orderBase, leadDays) : null;

  // Pack size for a line: the catalogue's, falling back to what the PO detail
  // carried (so an existing line renders correctly before products load).
  const packFor = (l: DraftLine): number | null =>
    packSizeOf(productByKey.get(l.ink_key)) ?? packSizeOf(po?.lines?.find((x) => x.id === l.id));

  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Per-line problems the operator can fix before saving (the server enforces
  // the same rules — these are the round-up hints).
  const lineIssue = (l: DraftLine): string | null => {
    if (!l.ink_key) return null;
    const pack = packFor(l);
    if (pack) {
      const t = parseFloat(l.tubs);
      if (!l.tubs || !Number.isFinite(t) || t <= 0) return null;
      if (!Number.isInteger(t)) return `Whole tubs only — round up to ${Math.ceil(t)} (${kg(Math.ceil(t) * pack)})`;
    }
    const q = lineKg(l, pack);
    if (l.received != null && q > 0 && q < l.received) return `Can't go below the ${kg(l.received)} already received`;
    return null;
  };

  const save = useMutation({
    mutationFn: async (issue: boolean) => {
      if (!supplier) throw new Error('Pick a supplier');
      const bad = lines.map((l) => lineIssue(l)).find(Boolean);
      if (bad) throw new Error(bad);
      const payloadLines: PoLineInput[] = lines
        .filter((l) => l.ink_key && lineKg(l, packFor(l)) > 0)
        .map((l) => ({
          id: l.id,
          ink_key: l.ink_key,
          qty_ordered_kg: lineKg(l, packFor(l)),
          unit_cost_per_kg: l.cost === '' ? null : l.cost,
          expected_date: l.expected || null,
        }));
      if (payloadLines.length === 0) throw new Error('Add at least one line with an ink and a quantity');
      const body: InkPoInput = {
        supplier_code: supplier, expected_date: expectedDate || null, notes: notes || null,
        lines: payloadLines, issue,
      };
      if (mode === 'edit' && po) {
        const saved = await updateInkPurchaseOrder(po.id, body);
        // A draft saved with "Save & issue" is placed straight after saving.
        return issue && saved.status === 'draft' ? issueInkPurchaseOrder(po.id) : saved;
      }
      return createInkPurchaseOrder(body);
    },
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });

  // Fill from shortfall: for the chosen supplier, every ink whose OUTSTANDING
  // demand exceeds free stock + on order, rounded UP to whole tubs. The operator
  // edits before issuing.
  //
  // Two deliberate choices, both matching the Balance screen so the two screens
  // can never disagree about what is short:
  //   * OUTSTANDING, not gross — ink already kitted onto an open trolley run has
  //     done its job; buying more for it would double-order (Jack, 11/09).
  //   * store_kg, not on_hand_kg — trolley stock is committed to the very run
  //     whose demand was just netted off, so counting it as cover would credit
  //     the same tubs twice.
  const fill = useMutation({
    mutationFn: async () => {
      if (!supplier) throw new Error('Pick a supplier first — the shortfall is per supplier');
      const [demand, stock, onOrder, series] = await Promise.all([
        getInkDemandSummary(), getInkStock({ materialClass: 'ink' }), getInkOnOrder(), listInkSeries(true),
      ]);
      const seriesSupplier = new Map<string, string | null>();
      series.forEach((s) => seriesSupplier.set(s.series_suffix, s.supplier_code ?? null));
      const demandBy = new Map<string, number>();
      const jobsBy = new Map<string, InkDemandJob[]>();
      demand.inks.forEach((r) => {
        if (!r.ink_key) return;                       // unassigned separation — no SKU to order
        const need = Number(r.outstanding_kg ?? r.total_kg ?? 0);
        demandBy.set(r.ink_key, (demandBy.get(r.ink_key) ?? 0) + need);
        if (r.jobs?.length) jobsBy.set(r.ink_key, [...(jobsBy.get(r.ink_key) ?? []), ...r.jobs]);
      });
      const freeStock = new Map<string, number>();
      stock.stock.forEach((s) => freeStock.set(s.ink_key, Number(s.store_kg || 0)));

      const out: DraftLine[] = [];
      const covered = new Map<string, { job: InkDemandJob; inks: Set<string> }>();
      demandBy.forEach((need, key) => {
        const prod = productByKey.get(key);
        if (!prod) return;
        const sup = prod.supplier_code || seriesSupplier.get(prod.series_suffix) || null;
        if (sup !== supplier) return;
        const short = need - (freeStock.get(key) ?? 0) - (onOrder[key] ?? 0);
        if (short <= 0) return;
        const pack = packSizeOf(prod);
        const n = pack ? Math.ceil(short / pack - 1e-9) : null;
        out.push({
          key: `l${_lk++}`, ink_key: key,
          tubs: n != null ? String(n) : '',
          kg: pack ? String(round1(n! * pack)) : String(Math.ceil(short * 10) / 10),
          cost: prod.standard_cost_per_kg != null ? String(prod.standard_cost_per_kg) : '',
          expected: '',
        });
        // WHICH jobs this line is really for — Jack, 11/09. A PO that says
        // "19 tubs of PMS 2035" is a number; "19 tubs, for job 284165 Mars
        // Celebrations, due 9 Oct" is a decision you can sanity-check.
        (jobsBy.get(key) ?? []).forEach((j) => {
          const e = covered.get(j.job_number) ?? { job: j, inks: new Set<string>() };
          e.inks.add(key);
          covered.set(j.job_number, e);
        });
      });
      out.sort((a, b) => a.ink_key.localeCompare(b.ink_key));
      return { out, covered: Array.from(covered.values()) };
    },
    onSuccess: ({ out, covered }) => {
      setError(null);
      setCoveredJobs(covered);
      // Stamp WHAT THIS ORDER IS FOR into the notes, because the panel below is
      // a sanity-check at the point of decision and vanishes when the dialog
      // closes — nothing else on the PO records why it exists (Jack, 15/09).
      //
      // Sheets are in it deliberately. If an order turns out wrong it is far
      // more likely to be a late volume change than a change of SKU mix, and
      // "284165 · 75,000 sh" is what makes that visible a month later when the
      // job has been reconciled and has dropped out of the demand figures
      // entirely. Plain text, not a foreign key: it is a snapshot of a forecast
      // taken on a date, and it must read as one rather than as a live link.
      if (covered.length > 0) setNotes(shortfallStamp(covered));
      if (out.length === 0) {
        setInfo(`No shortfall for ${supplier} — every ${supplier} ink's outstanding demand is covered by free stock and open orders.`);
        return;
      }
      setLines(out);
      setInfo(`${out.length} line${out.length === 1 ? '' : 's'} filled from shortfall (outstanding demand − free stock − on order), rounded up to whole tubs. Edit before issuing.`);
    },
    onError: (e) => setError((e as Error).message),
  });

  const orderedValue = lines.reduce((t, l) => t + lineKg(l, packFor(l)) * (parseFloat(l.cost) || 0), 0);
  const orderedKg = lines.reduce((t, l) => t + lineKg(l, packFor(l)), 0);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReceiptLongRounded sx={{ color: TOKENS.accent }} />
          {amend ? `Amend ${po?.po_number}` : mode === 'edit' ? `Edit ${po?.po_number}` : 'New purchase order'}
          {po && <StatusChip status={po.status} />}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={onClose}><CloseRounded fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {amend && (
          <Alert severity="info" sx={{ mb: 1.5, fontSize: 12.5 }}>
            This PO is issued. You can change each line’s <b>expected date</b>, <b>quantity</b> and <b>$/kg</b>
            (as the supplier confirms them) plus the PO’s expected date. Inks and received quantities stay as they are.
          </Alert>
        )}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="flex-start">
          <TextField select size="small" label="Supplier" value={supplier} disabled={amend}
            onChange={(e) => setSupplier(e.target.value)} sx={{ minWidth: 220 }} required>
            {(suppliersQ.data ?? []).map((s) => <MenuItem key={s.supplier_code} value={s.supplier_code}>{s.supplier_code} — {s.name}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Order date" value={po?.order_date ? dateOf(po.order_date) : 'Set when issued'}
            InputProps={{ readOnly: true }} disabled sx={{ width: 150 }}
            helperText="automatic" FormHelperTextProps={{ sx: { mx: 0.5 } }} />
          <TextField size="small" type="date" label="Expected" InputLabelProps={{ shrink: true }}
            value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} sx={{ width: 180 }}
            helperText={
              defaultExpected
                ? `blank = ${dateOf(defaultExpected)} (order date + ${leadDays} d lead time)`
                : supplier ? 'no lead time on this supplier' : ' '
            }
            FormHelperTextProps={{ sx: { mx: 0.5, whiteSpace: 'nowrap' } }} />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.ink3 }}>LINES</Typography>
          <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>— quantities are whole tubs; kg follows the SKU’s pack size</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {!amend && (
            <Tooltip title="Pre-fill lines for this supplier from demand − on hand − on order, rounded up to whole tubs">
              <span>
                <Button size="small" startIcon={fill.isPending ? <CircularProgress size={14} /> : <AutoFixHighRounded />}
                  disabled={!supplier || fill.isPending || productsQ.isLoading}
                  onClick={() => { setInfo(null); fill.mutate(); }} sx={{ color: TOKENS.accent, fontSize: 11.5 }}>
                  Fill from shortfall
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack spacing={1}>
          {lines.map((l) => {
            const prod = productByKey.get(l.ink_key);
            const pack = packFor(l);
            const issue = lineIssue(l);
            const q = lineKg(l, pack);
            return (
              <Box key={l.key}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Autocomplete
                    size="small" sx={{ flexGrow: 1, minWidth: 240 }}
                    options={products} disabled={amend}
                    getOptionLabel={(p) => `${p.ink_key} — ${p.description}`}
                    value={prod ?? null}
                    onChange={(_e, p) => setLine(l.key, {
                      ink_key: p?.ink_key ?? '',
                      cost: l.cost || (p?.standard_cost_per_kg != null ? String(p.standard_cost_per_kg) : ''),
                    })}
                    renderInput={(params) => <TextField {...params} label="Ink" placeholder="Search SKU…" />}
                    isOptionEqualToValue={(a, b) => a.ink_key === b.ink_key}
                  />
                  {pack || !l.ink_key ? (
                    <TextField size="small" label="Tubs" type="number" value={l.tubs}
                      onChange={(e) => setLine(l.key, { tubs: e.target.value })}
                      inputProps={{ step: 1, min: 0 }} sx={{ width: 90 }} disabled={!l.ink_key} />
                  ) : (
                    <TextField size="small" label="Qty (kg)" type="number" value={l.kg}
                      onChange={(e) => setLine(l.key, { kg: e.target.value })}
                      inputProps={{ step: 0.1, min: 0 }} sx={{ width: 100 }} />
                  )}
                  <Box sx={{ width: 96, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: q > 0 ? TOKENS.ink : TOKENS.ink3 }}>
                      {q > 0 ? kg(q) : '— kg'}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: TOKENS.ink3, lineHeight: 1.1 }}>
                      {pack ? `× ${fmt1(pack)} kg/tub` : l.ink_key ? 'no pack size' : ''}
                    </Typography>
                  </Box>
                  <TextField size="small" label="$/kg" type="number" value={l.cost}
                    onChange={(e) => setLine(l.key, { cost: e.target.value })}
                    inputProps={{ step: 0.1, min: 0 }} sx={{ width: 90 }} />
                  <TextField size="small" type="date" label="Expected" InputLabelProps={{ shrink: true }}
                    value={l.expected} onChange={(e) => setLine(l.key, { expected: e.target.value })}
                    sx={{ width: 150 }} />
                  {!amend && (
                    <IconButton size="small" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                {(issue || (l.ink_key && !pack) || (amend && l.received != null && l.received > 0)) && (
                  <Typography sx={{ fontSize: 11, color: issue ? WARN : TOKENS.ink3, mt: 0.25, ml: 0.5 }}>
                    {issue
                      ?? (amend && l.received ? `${kg(l.received)} already received on this line. ` : '')
                        + (l.ink_key && !pack ? 'This SKU has no pack size — enter the kg directly.' : '')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
        {!amend && (
          <Button size="small" startIcon={<AddRounded />} onClick={() => setLines((ls) => [...ls, newLine()])} sx={{ mt: 1, color: TOKENS.accent }}>
            Add line
          </Button>
        )}


        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>Ordered — {kg(orderedKg)} (est. value)</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{money(orderedValue)}</Typography>
        </Stack>

        {info && <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setInfo(null)}>{info}</Alert>}

        {/* What this order is actually FOR. A shortfall fill is derived from a
            set of real jobs, and a buyer signing off 19 tubs of a Pantone should
            be able to see the job and the due date behind it (Jack, 11/09). */}
        {coveredJobs.length > 0 && (
          <Box sx={{ mt: 1.5, border: `1px solid ${TOKENS.line}`, borderRadius: '10px', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="baseline" spacing={1}
              sx={{ px: 1.5, py: 1, bgcolor: TOKENS.lineSoft }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: TOKENS.ink3 }}>
                Covering
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                {coveredJobs.length} job{coveredJobs.length === 1 ? '' : 's'} waiting on these inks
                <Box component="span" sx={{ color: TOKENS.ink3 }}>
                  {' — saved onto the order, so it’s still here next month'}
                </Box>
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setCoveredJobs([])} sx={{ color: TOKENS.ink3, fontSize: 11 }}>
                Hide
              </Button>
            </Stack>
            <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
              <TableHead>
                <TableRow>
                  {['Job', 'Customer', 'Item', 'Sheets', 'Inks on this order'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 10.5, color: TOKENS.ink3 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...coveredJobs]
                  .sort((a, b) => a.job.job_number.localeCompare(b.job.job_number))
                  .map(({ job, inks }) => (
                    <TableRow key={job.job_number}>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12 }}>
                        {job.job_number}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{job.customer_code}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: TOKENS.ink2 }}>{job.customer_ref ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {job.run_length_sheets != null
                          ? `${job.run_length_sheets.toLocaleString()} sh`
                          : <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {Array.from(inks).sort().map((k) => (
                            <Chip key={k} size="small" label={k}
                              sx={{ height: 18, fontSize: 10, fontFamily: TOKENS.monoFont, fontWeight: 700,
                                bgcolor: TOKENS.lineSoft }} />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: TOKENS.ink3 }}>Cancel</Button>
        {amend ? (
          <Button variant="contained" disabled={save.isPending} onClick={() => { setError(null); save.mutate(false); }}
            sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
            {save.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save amendment'}
          </Button>
        ) : (
          <>
            <Button variant="outlined" disabled={save.isPending} onClick={() => { setError(null); save.mutate(false); }}
              sx={{ color: TOKENS.accent, borderColor: `${TOKENS.accent}80` }}>
              Save draft
            </Button>
            <Button variant="contained" disabled={save.isPending} onClick={() => { setError(null); save.mutate(true); }}
              sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
              {save.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save & issue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// PO detail — lines with received progress; issue / amend / cancel / receive.
// ---------------------------------------------------------------------------

interface ReceiveTarget { id: number; ink_key: string; outstanding: number; pack_size: number | null; cost: number | null }

function PoDetailDialog({ poId, onClose, onChanged, onEdit }: {
  poId: number;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (po: Po) => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: productionKeys.inkPurchaseOrder(poId), queryFn: () => getInkPurchaseOrder(poId), staleTime: 10 * 1000 });
  const po = q.data as Po | undefined;
  const [receiveLine, setReceiveLine] = useState<ReceiveTarget | null>(null);
  const [lastReceipt, setLastReceipt] = useState<string | null>(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: productionKeys.inkPurchaseOrder(poId) }); onChanged(); };

  const transition = useMutation({
    mutationFn: (action: 'issue' | 'cancel') => (action === 'issue' ? issueInkPurchaseOrder(poId) : cancelInkPurchaseOrder(poId)),
    onSuccess: refresh,
  });

  const th = (label: string, align: 'left' | 'right' = 'left') =>
    <TableCell align={align} sx={{ fontWeight: 700, fontSize: 11, color: TOKENS.ink3 }}>{label}</TableCell>;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="span" sx={{ fontFamily: TOKENS.monoFont }}>{po?.po_number ?? `PO ${poId}`}</Box>
          {po && <StatusChip status={po.status} />}
          <Box sx={{ flexGrow: 1 }} />
          {/* The F-093 document, in THIS tab.
              It used to window.open a new one, which landed on /login: the
              session token lives in sessionStorage, which is per-tab, and
              'noopener' severs the new context from this one so it starts with
              an EMPTY sessionStorage rather than a copy. Same-tab navigation
              keeps the session; the document has its own Back button. */}
          <Button size="small" startIcon={<LocalPrintshopRounded />}
            onClick={() => navigate(`/production/ink-purchase-order-document?po=${poId}`)}
            sx={{ color: TOKENS.accent }}>
            Print order
          </Button>
          <IconButton size="small" onClick={onClose}><CloseRounded fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {q.isLoading ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
          : q.isError || !po ? <Alert severity="error">Couldn’t load this PO.</Alert>
            : (
              <>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Meta label="Supplier" value={po.supplier_code} />
                  <Meta label="Order date" value={po.order_date ? dateOf(po.order_date) : <Box component="span" sx={{ color: TOKENS.ink3, fontWeight: 500 }}>set on issue</Box>} />
                  <Meta label="Expected" value={dateOf(po.expected_date)}
                    sub={po.supplier_lead_time_days != null ? `${po.supplier_lead_time_days} d lead time` : undefined} />
                  <Meta label="Ordered value" value={money(po.total_value)} />
                  <Meta label="Outstanding" value={kg(po.outstanding_kg)} />
                </Stack>

                {po.status === 'issued' && (
                  <Typography sx={{ fontSize: 12, color: TOKENS.ink2, mb: 1 }}>
                    <LocalShippingRounded sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5, color: TOKENS.accent }} />
                    Delivery arrived? Press <b>Receive</b> on the line — it books a batch into the store and draws the line down.
                  </Typography>
                )}
                {lastReceipt && <Alert severity="success" sx={{ mb: 1, fontSize: 12.5 }} onClose={() => setLastReceipt(null)}>{lastReceipt}</Alert>}

                <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
                  <TableHead>
                    <TableRow>
                      {th('Ink')}
                      {th('Ordered', 'right')}
                      {th('Received', 'right')}
                      {th('Outstanding', 'right')}
                      {th('Expected')}
                      {th('$/kg', 'right')}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(po.lines ?? []).map((l) => {
                      const outstanding = Number(l.outstanding_kg ?? 0);
                      const ownDate = !!l.expected_date;
                      return (
                        <TableRow key={l.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: l.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.18)' }} />
                              <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12 }}>{l.ink_key}</Box>
                              <Typography sx={{ fontSize: 11, color: TOKENS.ink3 }}>{l.description}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
                            {kg(l.qty_ordered_kg)}
                            <Sub>{tubs(l.packs_ordered)}</Sub>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: TOKENS.ink3 }}>
                            {kg(l.qty_received_kg)}
                            <Sub>{tubs(l.packs_received)}</Sub>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700, color: outstanding > 0 ? TOKENS.ink : '#2e7d32' }}>
                            {outstanding > 0 ? kg(outstanding) : 'in full'}
                            {outstanding > 0 && <Sub>{tubs(l.packs_outstanding)}</Sub>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: ownDate ? TOKENS.ink : TOKENS.ink3, whiteSpace: 'nowrap' }}>
                            {dateOf(l.effective_expected_date)}
                            {!ownDate && l.effective_expected_date && <Sub>as PO</Sub>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: TOKENS.ink3, whiteSpace: 'nowrap' }}>{perKg(l.unit_cost_per_kg)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {po.status === 'issued' && outstanding > 0 && (
                              <Button size="small" variant="contained" disableElevation startIcon={<LocalShippingRounded sx={{ fontSize: 14 }} />}
                                onClick={() => setReceiveLine({ id: l.id!, ink_key: l.ink_key, outstanding, pack_size: packSizeOf(l), cost: l.unit_cost_per_kg })}
                                sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong }, fontSize: 11, py: 0.25, px: 1.25 }}>
                                Receive
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {po.notes && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                      textTransform: 'uppercase', color: TOKENS.ink3, mb: 0.5 }}>
                      What this order is for
                    </Typography>
                    {/* pre-wrap + mono: the shortfall stamp is a small aligned
                        table, and collapsing its whitespace destroys it. */}
                    <Typography sx={{
                      fontSize: 11.5, color: TOKENS.ink2, whiteSpace: 'pre-wrap',
                      fontFamily: TOKENS.monoFont, lineHeight: 1.6,
                    }}>
                      {po.notes}
                    </Typography>
                  </Box>
                )}
              </>
            )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {po?.status === 'draft' && (
          <>
            <Button onClick={() => onEdit(po)} sx={{ color: TOKENS.ink2 }}>Edit</Button>
            <Button color="error" disabled={transition.isPending} onClick={() => transition.mutate('cancel')}>Cancel PO</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" disabled={transition.isPending} onClick={() => transition.mutate('issue')}
              sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
              {transition.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Issue (place order)'}
            </Button>
          </>
        )}
        {po?.status === 'issued' && (
          <>
            <Tooltip title="Change expected dates, quantities or $/kg as the supplier confirms them">
              <Button onClick={() => onEdit(po)} sx={{ color: TOKENS.ink2 }}>Amend</Button>
            </Tooltip>
            <Tooltip title="Cancel the remaining outstanding order">
              <Button color="error" disabled={transition.isPending} onClick={() => transition.mutate('cancel')}>Cancel PO</Button>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={onClose} sx={{ color: TOKENS.ink3 }}>Close</Button>
          </>
        )}
        {(po?.status === 'received' || po?.status === 'cancelled') && (
          <Button onClick={onClose} sx={{ color: TOKENS.ink3 }}>Close</Button>
        )}
      </DialogActions>

      {receiveLine && po && (
        <ReceiveDialog line={receiveLine} supplier={po.supplier_code}
          onClose={() => setReceiveLine(null)}
          onDone={(batches) => {
            const b = batches[0];
            if (b) {
              const after = Number(b.outstanding_after_kg ?? 0);
              setLastReceipt(
                `Received ${kg(b.qty_received_kg)} of ${b.ink_key} as batch ${b.batch_no} — `
                + (after > 0 ? `${kg(after)} still outstanding on this line.` : 'line complete.')
                + (b.po_status === 'received' ? ' PO fully received.' : ''),
              );
            }
            setReceiveLine(null);
            refresh();
          }} />
      )}
    </Dialog>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  if (children == null || children === '') return null;
  return <Typography sx={{ fontSize: 10, color: TOKENS.ink3, lineHeight: 1.1 }}>{children}</Typography>;
}

function Meta({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: TOKENS.ink3 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3 }}>{sub}</Typography>}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Receive against a PO line — goods-in that draws the line down. Defaults to
// the outstanding quantity; over- and under-receipt are WARNED, never blocked:
// the receipt is what physically arrived.
// ---------------------------------------------------------------------------

function ReceiveDialog({ line, supplier, onClose, onDone }: {
  line: ReceiveTarget;
  supplier: string;
  onClose: () => void;
  onDone: (batches: ReceiptBatch[]) => void;
}) {
  const qc = useQueryClient();
  const [packSize, setPackSize] = useState(line.pack_size ? String(line.pack_size) : '');
  const [packCount, setPackCount] = useState(line.pack_size ? String(Math.ceil(line.outstanding / line.pack_size - 1e-9)) : '');
  const [kgManual, setKgManual] = useState(String(round1(line.outstanding)));
  const [supplierBatch, setSupplierBatch] = useState('');
  const [cost, setCost] = useState(line.cost == null ? '' : String(line.cost));
  const [expiry, setExpiry] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pack = parseFloat(packSize);
  const byPacks = Number.isFinite(pack) && pack > 0;
  const n = parseFloat(packCount);
  const qty = byPacks
    ? (Number.isFinite(n) && n > 0 ? round1(n * pack) : 0)
    : (parseFloat(kgManual) || 0);

  const outstandingTubs = line.pack_size ? line.outstanding / line.pack_size : null;
  const variance = qty - line.outstanding;
  const warn: null | { kind: 'over' | 'partial'; text: string } =
    qty <= 0 ? null
      : variance > 0.05 ? { kind: 'over', text: `Over-receipt: ${kg(variance)} more than the ${kg(line.outstanding)} outstanding on this line.` }
        : variance < -0.05 ? { kind: 'partial', text: `Partial delivery — ${kg(-variance)} will still be outstanding after this receipt.` }
          : null;

  const receive = useMutation({
    mutationFn: async () => {
      if (!(qty > 0)) throw new Error('Enter a quantity greater than zero');
      if (byPacks && !Number.isInteger(n)) throw new Error('Tubs must be a whole number');
      if (warn && !confirm) throw new Error('Tick the confirmation to book a quantity that differs from what’s outstanding');
      return createInkReceipt({
        supplier_code: supplier,
        items: [{
          ink_key: line.ink_key,
          qty_kg: qty,
          pack_size_kg: byPacks ? pack : null,
          pack_count: byPacks ? n : null,
          unit_cost_per_kg: cost === '' ? null : Number(cost),
          supplier_batch_code: supplierBatch || null,
          expiry_date: expiry || null,
          po_line_id: line.id,
        }],
      }) as Promise<ReceiptBatch[]>;
    },
    onSuccess: (batches) => {
      // Goods-in moves stock + the ledger + on-order + this PO.
      ['ink-stock', 'ink-batches', 'ink-ledger', 'ink-purchase-orders', 'ink-on-order'].forEach((k) =>
        qc.invalidateQueries({ queryKey: ['production', k] }));
      onDone(batches);
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocalShippingRounded sx={{ color: TOKENS.accent }} />
          <span>Receive {line.ink_key}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1.25, mb: 1.5, borderRadius: '8px', bgcolor: TOKENS.lineSoft }}>
          <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: TOKENS.ink3 }}>Outstanding on this line</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>
            {kg(line.outstanding)}
            {outstandingTubs != null && <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.ink3, ml: 1 }}>{tubs(outstandingTubs)} of {fmt1(line.pack_size!)} kg</Box>}
          </Typography>
        </Box>
        <Stack spacing={1.5}>
          {byPacks || line.pack_size ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField size="small" label="Tubs received" type="number" value={packCount} autoFocus
                onChange={(e) => { setPackCount(e.target.value); setConfirm(false); }} inputProps={{ step: 1, min: 0 }} sx={{ flex: 1 }} />
              <TextField size="small" label="kg per tub" type="number" value={packSize}
                onChange={(e) => { setPackSize(e.target.value); setConfirm(false); }} inputProps={{ step: 0.1, min: 0 }} sx={{ width: 110 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right' }}>= {kg(qty)}</Typography>
            </Stack>
          ) : (
            <>
              <TextField size="small" label="Quantity received (kg)" type="number" value={kgManual} autoFocus
                onChange={(e) => { setKgManual(e.target.value); setConfirm(false); }} inputProps={{ step: 0.1, min: 0 }} />
              <Typography sx={{ fontSize: 11, color: TOKENS.ink3, mt: -1 }}>This SKU has no pack size — enter the kg directly.</Typography>
            </>
          )}
          <TextField size="small" label="Supplier batch code" value={supplierBatch}
            onChange={(e) => setSupplierBatch(e.target.value)} placeholder="THEIR code — the recall trace" />
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" label="$/kg" type="number" value={cost}
              onChange={(e) => setCost(e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ flex: 1 }} />
            <TextField size="small" type="date" label="Expiry" InputLabelProps={{ shrink: true }}
              value={expiry} onChange={(e) => setExpiry(e.target.value)} sx={{ flex: 1 }} />
          </Stack>
        </Stack>

        {warn && (
          <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '8px', bgcolor: 'rgba(237,176,0,0.12)', border: '1px solid rgba(178,106,0,0.35)' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: WARN }}>{warn.text}</Typography>
            <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.25 }}>
              {warn.kind === 'over'
                ? 'The line will show fully received. Book it if that is what arrived.'
                : 'The rest of the line stays on order; receive it when it arrives.'}
            </Typography>
            <FormControlLabel sx={{ mt: 0.5, '& .MuiFormControlLabel-label': { fontSize: 12.5, color: WARN, fontWeight: 600 } }}
              control={<Checkbox size="small" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} sx={{ color: WARN, '&.Mui-checked': { color: WARN } }} />}
              label={`Yes — ${kg(qty)} is what physically arrived`} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: TOKENS.ink3 }}>Cancel</Button>
        <Button variant="contained" disabled={receive.isPending || !(qty > 0) || (!!warn && !confirm)}
          onClick={() => { setError(null); receive.mutate(); }}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          {receive.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Receive ${qty > 0 ? kg(qty) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
